/**
 * Enhanced Member Action Plan Component
 *
 * Key improvements:
 * 1. Actions displayed prominently at top, prediction summary below
 * 2. Integrated with internal communication tools (not external links)
 * 3. Actions can be converted to tasks
 * 4. All communications are logged to member conversations
 *
 * Trust Marketplace - Member Retention Intelligence
 */

import React, { useState, useMemo } from 'react';
import {
  Phone,
  MessageSquare,
  Mail,
  Home,
  Calendar,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Info,
  Clock,
  ChevronDown,
  ChevronUp,
  Copy,
  ClipboardList,
  Flag,
  Plus,
  Brain,
  TrendingUp,
  TrendingDown,
  Loader2,
} from 'lucide-react';
import { config } from '@/src/config';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ContributingFactor {
  factor: string;
  value: number | string;
  importance: number;
  impact: 'high' | 'medium' | 'low';
}

interface MemberData {
  member_id: number;
  first_name: string;
  last_name: string;
  phone_number?: string;
  email?: string;
  engagement_score: number;
  risk_level: string;
  trend_direction: string;
  days_since_last_contact?: number;
  days_since_last_activity?: number;
  communication_score: number;
  activity_score: number;
  recency_score: number;
  data_quality_score: number;
  preferred_channel?: string;
  best_day_of_week?: number;
  best_hour_start?: number;
  best_hour_end?: number;
  churn_probability?: number;
  contributing_factors?: ContributingFactor[];
  last_contact_outcome?: string;
  contact_attempts_7d?: number;
  program_name?: string;
  enrollment_id?: number;
}

interface ActionStep {
  id: number;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  action: string;
  method?: 'phone' | 'sms' | 'email' | 'home_visit' | 'task';
  timeframe: string;
  details: string;
  script?: string;
  completed?: boolean;
}

interface MemberActionPlanEnhancedProps {
  member: MemberData;
  compact?: boolean;
  onActionComplete?: (stepId: number) => void;
  // Internal communication handlers
  onOpenPhoneDialog?: (member: MemberData) => void;
  onOpenSmsDialog?: (member: MemberData) => void;
  onOpenEmailDialog?: (member: MemberData) => void;
  // Task creation handler
  onCreateTask?: (taskData: {
    title: string;
    description: string;
    priority: string;
    member_id: number;
    enrollment_id?: number;
    due_date?: string;
  }) => void;
  // Refresh handlers
  onCommunicationSent?: () => void;
}

// Days of week mapping
const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Channel icons
const getChannelIcon = (channel?: string) => {
  switch (channel?.toLowerCase()) {
    case 'phone':
    case 'phone_call':
      return <Phone className="h-4 w-4" />;
    case 'sms':
    case 'text':
      return <MessageSquare className="h-4 w-4" />;
    case 'email':
      return <Mail className="h-4 w-4" />;
    case 'home_visit':
      return <Home className="h-4 w-4" />;
    default:
      return <Phone className="h-4 w-4" />;
  }
};

// Priority colors
const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'urgent':
      return 'destructive';
    case 'high':
      return 'default';
    case 'medium':
      return 'secondary';
    default:
      return 'outline';
  }
};

// Priority badge variant
const getPriorityBadgeVariant = (priority: string): 'destructive' | 'default' | 'secondary' | 'outline' => {
  switch (priority) {
    case 'urgent':
      return 'destructive';
    case 'high':
      return 'default';
    case 'medium':
      return 'secondary';
    default:
      return 'outline';
  }
};

// Risk level colors
const getRiskColor = (riskLevel: string): string => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
    case 'lost':
      return 'text-red-600';
    case 'at_risk':
    case 'high':
      return 'text-orange-600';
    case 'moderate':
      return 'text-blue-600';
    case 'engaged':
    case 'low':
    default:
      return 'text-green-600';
  }
};

// Risk badge variant
const getRiskBadgeVariant = (riskLevel: string): 'destructive' | 'default' | 'secondary' | 'outline' => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
    case 'lost':
      return 'destructive';
    case 'at_risk':
    case 'high':
      return 'default';
    case 'moderate':
      return 'secondary';
    case 'engaged':
    case 'low':
    default:
      return 'outline';
  }
};

// Progress bar color
const getRiskProgressColor = (riskLevel: string): string => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical':
    case 'lost':
      return 'bg-red-600';
    case 'at_risk':
    case 'high':
      return 'bg-orange-600';
    case 'moderate':
      return 'bg-blue-600';
    case 'engaged':
    case 'low':
    default:
      return 'bg-green-600';
  }
};

// Generate action plan based on member data
const generateActionPlan = (member: MemberData): ActionStep[] => {
  const steps: ActionStep[] = [];
  let stepId = 1;

  const isUrgent = member.risk_level === 'critical' || (member.churn_probability && member.churn_probability > 0.7);
  const isHighRisk = member.risk_level === 'at_risk' || (member.churn_probability && member.churn_probability > 0.4);
  const daysSinceContact = member.days_since_last_contact || 0;
  const hasContactInfo = member.phone_number || member.email;
  const preferredChannel = member.preferred_channel || 'phone';

  // Step 1: Immediate action based on risk level
  if (isUrgent) {
    steps.push({
      id: stepId++,
      priority: 'urgent',
      action: 'Make immediate contact attempt',
      method: preferredChannel as any,
      timeframe: 'Within 4 hours',
      details: `${member.first_name} is at critical risk. Prioritize this member today.`,
      script: `"Hi ${member.first_name}, this is [Name] from [Organization]. I wanted to check in with you personally. How are you doing? Is there anything preventing you from participating in the ${member.program_name || 'program'}?"`,
    });
  } else if (isHighRisk) {
    steps.push({
      id: stepId++,
      priority: 'high',
      action: `Contact ${member.first_name} via ${preferredChannel}`,
      method: preferredChannel as any,
      timeframe: 'Within 24 hours',
      details: daysSinceContact > 7
        ? `It's been ${daysSinceContact} days since last contact. Re-establish connection.`
        : 'Engagement is declining. Check in to understand barriers.',
      script: `"Hi ${member.first_name}, this is [Name] calling to check in. I noticed it's been a little while since we connected. How have you been?"`,
    });
  } else if (daysSinceContact > 14) {
    steps.push({
      id: stepId++,
      priority: 'medium',
      action: 'Schedule routine check-in',
      method: preferredChannel as any,
      timeframe: 'Within 48 hours',
      details: `Regular engagement opportunity - ${daysSinceContact} days since last contact.`,
    });
  }

  const factors = member.contributing_factors || [];

  // Contact failures - try alternative method
  const consecutiveFailures = factors.find(f => f.factor.includes('consecutive_failures'));
  if (consecutiveFailures && Number(consecutiveFailures.value) >= 2) {
    steps.push({
      id: stepId++,
      priority: 'high',
      action: 'Try alternative contact method',
      method: preferredChannel === 'phone' ? 'sms' : 'phone',
      timeframe: 'If primary method fails',
      details: `${consecutiveFailures.value} previous contact attempts unsuccessful. Switch approach.`,
    });
  }

  // Low communication score
  if (member.communication_score < 40) {
    steps.push({
      id: stepId++,
      priority: 'medium',
      action: 'Verify contact information accuracy',
      method: 'task',
      timeframe: 'Before next contact attempt',
      details: 'Low communication score may indicate outdated or incorrect contact info.',
    });
  }

  // SDOH factors
  const sdohFactor = factors.find(f => f.factor.includes('sdoh') || f.factor.includes('poverty') || f.factor.includes('transportation'));
  if (sdohFactor && sdohFactor.impact === 'high') {
    steps.push({
      id: stepId++,
      priority: 'medium',
      action: 'Assess social determinant barriers',
      method: 'task',
      timeframe: 'During next conversation',
      details: 'Area has high SDOH risk. Ask about transportation, food security, housing, or other needs.',
      script: `"Are there any challenges you're facing that make it difficult to participate in the program?"`,
    });
  }

  // Declining engagement trend
  if (member.trend_direction === 'declining' || member.trend_direction === 'rapid_decline') {
    steps.push({
      id: stepId++,
      priority: member.trend_direction === 'rapid_decline' ? 'high' : 'medium',
      action: 'Discuss engagement barriers',
      method: 'phone',
      timeframe: 'During conversation',
      details: 'Engagement is trending down. Understand why and address concerns.',
      script: `"I noticed your participation has changed recently. Is everything okay?"`,
    });
  }

  // Low activity score
  if (member.activity_score < 40) {
    steps.push({
      id: stepId++,
      priority: 'medium',
      action: 'Re-engage with program activities',
      method: 'task',
      timeframe: 'After initial contact',
      details: 'Schedule upcoming appointments or activities to boost engagement.',
    });
  }

  // Data quality issues
  if (member.data_quality_score < 50) {
    steps.push({
      id: stepId++,
      priority: 'low',
      action: 'Update member profile',
      method: 'task',
      timeframe: 'During conversation',
      details: 'Confirm/update address, phone, email, and emergency contacts.',
    });
  }

  // Follow-up step
  steps.push({
    id: stepId++,
    priority: 'low',
    action: 'Schedule follow-up',
    method: 'task',
    timeframe: isUrgent ? '3 days' : '7 days',
    details: `Set reminder to follow up and track progress.`,
  });

  // Home visit for lost or unresponsive members
  if (member.risk_level === 'lost' || (daysSinceContact > 30 && !hasContactInfo)) {
    steps.splice(1, 0, {
      id: 999,
      priority: 'high',
      action: 'Consider home visit',
      method: 'home_visit',
      timeframe: 'If contact attempts fail',
      details: 'Extended period without contact. Home visit may be needed for welfare check.',
    });
  }

  // Renumber steps
  return steps.map((step, index) => ({ ...step, id: index + 1 }));
};

// Format optimal contact time
const formatOptimalTime = (member: MemberData): string => {
  const day = member.best_day_of_week !== undefined ? daysOfWeek[member.best_day_of_week] : 'Weekdays';
  const startHour = member.best_hour_start || 9;
  const endHour = member.best_hour_end || 17;
  const formatHour = (h: number) => {
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}${period}`;
  };
  return `${day}, ${formatHour(startHour)} - ${formatHour(endHour)}`;
};

// Format factor name for display
const formatFactorName = (factor: string): string => {
  return factor
    .replace(/^f_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export const MemberActionPlanEnhanced: React.FC<MemberActionPlanEnhancedProps> = ({
  member,
  compact = false,
  onActionComplete,
  onOpenPhoneDialog,
  onOpenSmsDialog,
  onOpenEmailDialog,
  onCreateTask,
  onCommunicationSent,
}) => {
  const [expanded, setExpanded] = useState(!compact);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [showPredictionDetails, setShowPredictionDetails] = useState(false);
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<ActionStep | null>(null);
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [sendingCommunication, setSendingCommunication] = useState(false);

  const actionPlan = useMemo(() => generateActionPlan(member), [member]);

  const handleComplete = (stepId: number) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
    onActionComplete?.(stepId);
  };

  // Handle internal phone call
  const handleCallMember = async () => {
    if (onOpenPhoneDialog) {
      onOpenPhoneDialog(member);
    } else {
      // Fallback: Use internal API to initiate call
      try {
        setSendingCommunication(true);
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');

        const response = await fetch(`${config.weaveinApi.baseUrl}/api/members/${member.member_id}/communications/phone`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || '',
          },
          body: JSON.stringify({
            to_number: member.phone_number,
            enrollment_id: member.enrollment_id,
            member_id: member.member_id,
            call_type: 'retention_outreach',
            context_message: `Retention outreach call for ${member.first_name} ${member.last_name}`,
          }),
        });

        if (response.ok) {
          // BYOP providers that can't originate a call server-side (Zoom Phone)
          // return a dial URI for the staff member's own softphone to open.
          const result = await response.json().catch(() => null);
          if (result?.dial_uri) {
            window.location.href = result.dial_uri;
          }
          onCommunicationSent?.();
        }
      } catch (error) {
        console.error('Failed to initiate call:', error);
      } finally {
        setSendingCommunication(false);
      }
    }
  };

  // Handle internal SMS
  const handleSendSMS = async () => {
    if (onOpenSmsDialog) {
      onOpenSmsDialog(member);
    } else {
      // Fallback: Use internal API to send SMS
      try {
        setSendingCommunication(true);
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');

        // Open SMS composer dialog or use API
        const response = await fetch(`${config.weaveinApi.baseUrl}/api/members/${member.member_id}/communications/sms`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || '',
          },
          body: JSON.stringify({
            to_number: member.phone_number,
            enrollment_id: member.enrollment_id,
            member_id: member.member_id,
            message_type: 'retention_outreach',
            // Message will be prompted in a dialog
          }),
        });

        if (response.ok) {
          onCommunicationSent?.();
        }
      } catch (error) {
        console.error('Failed to send SMS:', error);
      } finally {
        setSendingCommunication(false);
      }
    }
  };

  // Handle internal email
  const handleSendEmail = async () => {
    if (onOpenEmailDialog) {
      onOpenEmailDialog(member);
    } else {
      // Fallback: Use internal API to send email
      try {
        setSendingCommunication(true);
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');

        const response = await fetch(`${config.weaveinApi.baseUrl}/api/members/${member.member_id}/communications/email`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || '',
          },
          body: JSON.stringify({
            to_email: member.email,
            enrollment_id: member.enrollment_id,
            member_id: member.member_id,
            email_type: 'retention_outreach',
          }),
        });

        if (response.ok) {
          onCommunicationSent?.();
        }
      } catch (error) {
        console.error('Failed to send email:', error);
      } finally {
        setSendingCommunication(false);
      }
    }
  };

  // Handle creating task from action step
  const handleCreateTaskFromStep = (step: ActionStep) => {
    setSelectedStep(step);
    setTaskDescription(step.details);
    // Calculate due date based on timeframe
    const now = new Date();
    if (step.timeframe.includes('4 hours')) {
      now.setHours(now.getHours() + 4);
    } else if (step.timeframe.includes('24 hours')) {
      now.setDate(now.getDate() + 1);
    } else if (step.timeframe.includes('48 hours')) {
      now.setDate(now.getDate() + 2);
    } else if (step.timeframe.includes('3 days')) {
      now.setDate(now.getDate() + 3);
    } else if (step.timeframe.includes('7 days')) {
      now.setDate(now.getDate() + 7);
    } else {
      now.setDate(now.getDate() + 3);
    }
    setTaskDueDate(now.toISOString().split('T')[0]);
    setCreateTaskDialogOpen(true);
  };

  const handleConfirmCreateTask = () => {
    if (selectedStep && onCreateTask) {
      onCreateTask({
        title: selectedStep.action,
        description: taskDescription,
        priority: selectedStep.priority,
        member_id: member.member_id,
        enrollment_id: member.enrollment_id,
        due_date: taskDueDate,
      });
      handleComplete(selectedStep.id);
    }
    setCreateTaskDialogOpen(false);
    setSelectedStep(null);
    setTaskDescription('');
    setTaskDueDate('');
  };

  const urgentSteps = actionPlan.filter(s => s.priority === 'urgent' || s.priority === 'high');
  const hasUrgentActions = urgentSteps.length > 0;
  const percentDisplay = Math.round((member.churn_probability || 0) * 100);

  return (
    <TooltipProvider>
      <Card className={`border ${hasUrgentActions ? 'bg-orange-50' : 'bg-gray-50'}`}>
        <CardContent className={compact ? 'pb-2' : 'pb-4'}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="relative">
                <ClipboardList className="h-5 w-5 text-primary" />
                {hasUrgentActions && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] text-white font-bold">
                    {urgentSteps.length}
                  </span>
                )}
              </div>
              <h3 className="text-base font-bold">
                Action Plan for {member.first_name}
              </h3>
            </div>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy plan</TooltipContent>
              </Tooltip>
              {compact && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </div>

          {/* ==================== ACTIONS SECTION (TOP) ==================== */}

          {/* Urgent Alert with Quick Actions */}
          {hasUrgentActions && (
            <Alert className="mb-4 border-orange-600 bg-orange-50">
              <Flag className="h-4 w-4 text-orange-600" />
              <AlertDescription>
                <div className="flex items-center justify-between w-full">
                  <p className="text-sm font-medium">
                    {urgentSteps.length} urgent action{urgentSteps.length > 1 ? 's' : ''} required
                  </p>
                  <div className="flex gap-2">
                    {member.phone_number && (
                      <Button
                        size="sm"
                        onClick={handleCallMember}
                        disabled={sendingCommunication}
                      >
                        {sendingCommunication ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Phone className="h-4 w-4 mr-2" />
                        )}
                        Call
                      </Button>
                    )}
                    {member.phone_number && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSendSMS}
                        disabled={sendingCommunication}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        SMS
                      </Button>
                    )}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Quick Action Buttons (Always Visible) */}
          <div className="border rounded-lg p-3 mb-4 bg-background">
            <p className="text-xs text-muted-foreground font-medium mb-2">
              QUICK ACTIONS
            </p>
            <div className="flex gap-2 flex-wrap">
              {member.phone_number && (
                <Button
                  size="sm"
                  onClick={handleCallMember}
                  disabled={sendingCommunication}
                >
                  {sendingCommunication ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Phone className="h-4 w-4 mr-2" />
                  )}
                  Call Member
                </Button>
              )}
              {member.phone_number && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSendSMS}
                  disabled={sendingCommunication}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send SMS
                </Button>
              )}
              {member.email && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSendEmail}
                  disabled={sendingCommunication}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const defaultStep: ActionStep = {
                    id: 0,
                    priority: 'medium',
                    action: 'Custom follow-up task',
                    method: 'task',
                    timeframe: '7 days',
                    details: `Follow up with ${member.first_name} ${member.last_name}`,
                  };
                  handleCreateTaskFromStep(defaultStep);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Task
              </Button>
            </div>
          </div>

          {/* Action Steps List */}
          <div className="border rounded-lg p-3 mb-4">
            <p className="text-xs text-muted-foreground font-medium mb-2">
              RECOMMENDED ACTIONS ({actionPlan.length})
            </p>
            <ul className="space-y-2">
              {actionPlan.map((step, index) => (
                <li
                  key={step.id}
                  className={`flex items-start gap-3 p-2 rounded-md ${
                    completedSteps.has(step.id)
                      ? 'bg-green-50 opacity-70'
                      : 'bg-transparent'
                  }`}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div
                      className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        completedSteps.has(step.id)
                          ? 'bg-green-600'
                          : step.priority === 'urgent'
                          ? 'bg-red-600'
                          : step.priority === 'high'
                          ? 'bg-orange-600'
                          : step.priority === 'medium'
                          ? 'bg-blue-600'
                          : 'bg-green-600'
                      }`}
                    >
                      {completedSteps.has(step.id) ? (
                        <CheckCircle className="h-3.5 w-3.5" />
                      ) : (
                        index + 1
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p
                        className={`text-sm ${
                          step.priority === 'urgent' || step.priority === 'high'
                            ? 'font-bold'
                            : 'font-medium'
                        } ${
                          completedSteps.has(step.id) ? 'line-through' : ''
                        }`}
                      >
                        {step.action}
                      </p>
                      <Badge
                        variant={getPriorityBadgeVariant(step.priority)}
                        className="h-4 text-[9px] px-1.5"
                      >
                        {step.priority.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {step.timeframe} • {step.details}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {step.method && step.method !== 'task' && step.method !== 'home_visit' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              if (step.method === 'phone') handleCallMember();
                              else if (step.method === 'sms') handleSendSMS();
                              else if (step.method === 'email') handleSendEmail();
                            }}
                            disabled={completedSteps.has(step.id) || sendingCommunication}
                          >
                            {step.method === 'phone' ? (
                              <Phone className="h-4 w-4" />
                            ) : step.method === 'sms' ? (
                              <MessageSquare className="h-4 w-4" />
                            ) : (
                              <Mail className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {step.method === 'phone' ? 'Call' : step.method === 'sms' ? 'Send SMS' : 'Send Email'}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleCreateTaskFromStep(step)}
                          disabled={completedSteps.has(step.id)}
                        >
                          <ClipboardList className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Create task</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleComplete(step.id)}
                          disabled={completedSteps.has(step.id)}
                        >
                          <CheckCircle
                            className={`h-4 w-4 ${
                              completedSteps.has(step.id)
                                ? 'text-green-600'
                                : 'text-muted-foreground'
                            }`}
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Mark complete</TooltipContent>
                    </Tooltip>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {expanded && (
            <>
              {/* ==================== PREDICTION SUMMARY (BOTTOM) ==================== */}

              <hr className="my-4 border-border" />

              {/* Prediction Summary Header */}
              <div
                className="flex items-center justify-between cursor-pointer mb-2"
                onClick={() => setShowPredictionDetails(!showPredictionDetails)}
              >
                <div className="flex items-center gap-2">
                  <Brain className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-semibold">AI Prediction Summary</h4>
                </div>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {showPredictionDetails ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Churn Probability Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-muted-foreground">
                    14-Day Churn Probability
                  </p>
                  <Badge variant={getRiskBadgeVariant(member.risk_level)}>
                    {member.risk_level?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Progress
                      value={percentDisplay}
                      className="h-2"
                      indicatorClassName={getRiskProgressColor(member.risk_level)}
                    />
                  </div>
                  <p className={`text-sm font-bold ${getRiskColor(member.risk_level)}`}>
                    {percentDisplay}%
                  </p>
                </div>
              </div>

              {/* Contact Info Summary */}
              <div className="border rounded-lg p-3 mb-4 bg-background">
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm">
                      <strong>Best Time:</strong> {formatOptimalTime(member)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {getChannelIcon(member.preferred_channel)}
                    <p className="text-sm">
                      <strong>Preferred:</strong> {member.preferred_channel || 'Phone'}
                    </p>
                  </div>
                  {member.phone_number && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm">{member.phone_number}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Detailed Prediction Factors */}
              {showPredictionDetails && member.contributing_factors && member.contributing_factors.length > 0 && (
                <div className="border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground font-medium mb-2">
                    CONTRIBUTING FACTORS
                  </p>
                  <ul className="space-y-1">
                    {member.contributing_factors.slice(0, 5).map((factor, index) => (
                      <li key={index} className="flex items-center gap-2 py-0.5">
                        <div className="flex-shrink-0">
                          {factor.impact === 'high' ? (
                            <TrendingUp className="h-4 w-4 text-red-600" />
                          ) : factor.impact === 'medium' ? (
                            <TrendingDown className="h-4 w-4 text-orange-600" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        <p className="text-sm">
                          {formatFactorName(factor.factor)}:{' '}
                          <strong>
                            {typeof factor.value === 'number'
                              ? factor.value % 1 === 0
                                ? factor.value
                                : factor.value.toFixed(1)
                              : factor.value}
                          </strong>
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={createTaskDialogOpen} onOpenChange={setCreateTaskDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              Create Task from Action
            </DialogTitle>
          </DialogHeader>
          {selectedStep && (
            <div className="pt-4 space-y-4">
              <div>
                <h4 className="text-sm font-semibold mb-2">{selectedStep.action}</h4>
                <Badge variant={getPriorityBadgeVariant(selectedStep.priority)}>
                  {selectedStep.priority.toUpperCase()}
                </Badge>
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-description">Task Description</Label>
                <textarea
                  id="task-description"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-due-date">Due Date</Label>
                <Input
                  id="task-due-date"
                  type="date"
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTaskDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmCreateTask}>
              <Plus className="h-4 w-4 mr-2" />
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default MemberActionPlanEnhanced;
